import AuthPhase from "./phases/AuthPhase";
import CompletePhase from "./phases/CompletePhase";
import ConnectPhase from "./phases/ConnectPhase";
import ScanningPhase from "./phases/ScanningPhase";
import { ScannerStepper } from "./ScannerParts";
import { useScannerController } from "./useScannerController";

export interface ScannerPanelProps {
  /** Workspace version to sync against — required to run a real crawl. */
  versionId?: number;
  /** Project id (optional) — lets the scanner persist a changed URL back. */
  projectId?: number | null;
  /** Project object (optional) — used to prefill the app URL. */
  project?: { websiteurl?: string; versionId?: number } | null;
  /** Explicit initial URL (overrides project.websiteurl). */
  initialUrl?: string;
  /** Optional primary action shown on the complete step (e.g. "Generate tests"). */
  primaryAction?: { label: string; onClick: () => void };
  /** Called when the flow is dismissed or finished (Done / close). */
  onClose: () => void;
}

/**
 * The embeddable AI Application Scanner flow (Connect → Auth → Scan → Complete).
 * Renders only the panel content — no dialog chrome — so it can live inside the
 * standalone {@link ScannerDialog} OR inline in another dialog (e.g. the AI Test
 * Case Generator's "Re-scan Application"). Give it a `versionId` and it runs the
 * same site-sync crawl the project AI Sync tab uses.
 */
export function ScannerPanel({
  versionId,
  projectId,
  project,
  initialUrl,
  primaryAction,
  onClose,
}: ScannerPanelProps) {
  const c = useScannerController({ versionId, projectId, project, initialUrl });

  return (
    <div className="flex min-h-full w-full flex-col">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border bg-white px-5 py-2">
        <span className="truncate text-[11px] text-muted-foreground">
          {c.displayHost ? (
            <>
              Target{" "}
              <span className="font-mono text-foreground">{c.displayHost}</span>
            </>
          ) : (
            "Learning your app so tests write themselves"
          )}
        </span>
        <ScannerStepper phase={c.phase} />
      </div>

      <div className="flex-1 flex flex-col min-h-0">
        {c.phase === "connect" && <ConnectPhase c={c} />}
        {c.phase === "auth" && <AuthPhase c={c} />}
        {c.phase === "scanning" && <ScanningPhase c={c} onClose={onClose} />}
        {c.phase === "complete" && (
          <CompletePhase
            c={c}
            onClose={onClose}
            primaryAction={primaryAction}
          />
        )}
      </div>
    </div>
  );
}

export default ScannerPanel;
